// Capa HTTP del dominio "perfiles" (maestro de perfiles de usuario).
// Tipos + funciones fetch + clasificación de los rechazos del backend. El
// estado de la lista vive en TanStack Query (ver `src/hooks/usePerfiles.ts`),
// no acá — mismo patrón que `lib/usuarios.ts`.

import { apiFetch, ApiError, getApiErrorMessage, parseValidationErrors } from "./api";

const FALLBACK_BORRADO = "No pudimos eliminar el perfil. Inténtalo de nuevo.";

export interface Perfil {
  id: number;
  nombre: string;
  /** Código de la semilla (SA/UN/RV). `null` en los perfiles que crea el admin. */
  codigo: string | null;
  /** Derivado de `codigo` en el backend — no es columna. */
  es_semilla: boolean;
  /** Solo viene cuando el backend cuenta los usuarios asignados. */
  usuarios_count?: number;
}

export interface PerfilInput {
  nombre: string;
}

export async function fetchPerfiles(): Promise<Perfil[]> {
  const { data } = await apiFetch<{ data: Perfil[] }>("/api/perfiles");
  return data;
}

export async function crearPerfilRequest(input: PerfilInput): Promise<Perfil> {
  const { data } = await apiFetch<{ data: Perfil }>("/api/perfiles", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function actualizarPerfilRequest(
  id: number,
  input: PerfilInput,
): Promise<Perfil> {
  const { data } = await apiFetch<{ data: Perfil }>(`/api/perfiles/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/** 204 sin cuerpo cuando sale bien; 422 con motivo cuando el backend lo bloquea. */
export function eliminarPerfilRequest(id: number): Promise<void> {
  return apiFetch<void>(`/api/perfiles/${id}`, { method: "DELETE" });
}

/* ---------------------------------------------------------------------------
   Rechazos del borrado

   DELETE /api/perfiles/{id} tiene DOS rechazos y ambos son 422:

   1. perfil semilla → { message }                      (sin salida: no se puede)
   2. perfil en uso  → { message, usuarios_count: N }   (con salida: reasignar)

   El backend manda el conteo justamente para no tener que hacer una segunda
   consulta, y ES LO ÚNICO que separa un caso del otro: la respuesta no trae
   ningún discriminador de motivo. Por eso `en-uso` se reconoce POSITIVAMENTE
   (`usuarios_count` numérico) y lo demás NO se adivina como "semilla":
   un 422 con mensaje y sin conteo se clasifica como `bloqueado`, que es
   exactamente lo que se sabe —el servidor lo bloqueó y no dio salida—, sin
   afirmar por descarte un motivo que el contrato no informa.

   Esa diferencia importa porque la conducta cambia: `en-uso` ofrece
   reintentar, `bloqueado` retira la acción. Si un tercer rechazo apareciera
   mañana, cae en `bloqueado` (conservador y verdadero) y no en una etiqueta
   inventada. Función pura para poder probarla sin componentes.
--------------------------------------------------------------------------- */

export type RechazoDeBorrado =
  | { motivo: "bloqueado"; mensaje: string }
  | { motivo: "en-uso"; mensaje: string; usuariosCount: number }
  | { motivo: "desconocido"; mensaje: string };

export function clasificarRechazoDeBorrado(error: unknown): RechazoDeBorrado {
  if (error instanceof ApiError && error.status === 422) {
    let cuerpo: { message?: string; usuarios_count?: number } | null = null;
    try {
      cuerpo = JSON.parse(error.body) as { message?: string; usuarios_count?: number };
    } catch {
      // 422 con cuerpo no-JSON: no es ninguno de los dos del contrato.
      cuerpo = null;
    }

    if (cuerpo?.message) {
      return typeof cuerpo.usuarios_count === "number"
        ? { motivo: "en-uso", mensaje: cuerpo.message, usuariosCount: cuerpo.usuarios_count }
        : { motivo: "bloqueado", mensaje: cuerpo.message };
    }
  }

  return { motivo: "desconocido", mensaje: getApiErrorMessage(error, FALLBACK_BORRADO) };
}

/** Primer mensaje de validación del campo `nombre` en un 422; null si no aplica. */
export function errorDeNombreDePerfil(error: unknown): string | null {
  return parseValidationErrors(error)?.nombre ?? null;
}
